import { Alert, useTheme } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  DateLocalizer,
  EventWrapperProps,
  Formats,
  SlotInfo,
  Views,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { rrulestr } from 'rrule/dist/esm/rrulestr';
import { ServerPlatformUserRaw } from 'src/graphql';
import {
  OncallManagementScheduleMutationRecord,
  OncallManagementScheduleRecord,
  RecurrenceOptions,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { Modals, useShowModals } from 'src/store/modals';
import { pickColor } from 'src/utils/strings';
import {
  convertDateTimeToDate,
  isAllDaySchedule,
  isAllDaySlotSelected,
} from './helpers/dates-manipulation';
import { getMissingDstHour } from './helpers/dst-utils';
import {
  getDisplayedEventsForSelectedRange,
  mapScheduleToEvents,
} from './helpers/events-data-mappers';
import { localizer } from './helpers/localizer';

import {
  CalendarWrapper,
  EventWrapper,
  SignalHeader,
  SignalToolbar,
} from './helpers/ui-elements';
import { SignalOnCallScheduleModalProps } from './signal-oncall-schedule-modal';
import {
  CalendarEventObject,
  CalendarView,
  DEFAULT_DESKTOP_CALENDAR_VIEW,
  DEFAULT_MOBILE_CALENDAR_VIEW,
  DESKTOP_CALENDAR_VIEWS,
  isCalendarView,
  MOBILE_CALENDAR_VIEWS,
} from './types';

dayjs.extend(timezone);

const ALL_DAY_EVENT_HEADER = 'ALL DAY';

interface SignalOnCallScheduleCalendarProps {
  partnerId: number;
  events: OncallManagementScheduleRecord[];
  timezone: string;
  agents: ServerPlatformUserRaw[];
  canManageSchedules?: boolean;
  hasLoadError?: boolean;
  isMobile?: boolean;
  onCreateEvent: (event: OncallManagementScheduleMutationRecord) => void;
  onUpdateEvent: (event: OncallManagementScheduleMutationRecord) => void;
  onDeleteEvent: (eventId: string) => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}
export const SignalOnCallScheduleCalendar = ({
  partnerId,
  events: existingEvents,
  timezone,
  agents,
  canManageSchedules = true,
  hasLoadError = false,
  isMobile = false,
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent,
  selectedDate,
  onDateChange,
}: SignalOnCallScheduleCalendarProps) => {
  const [showSignalManagementModal] = useShowModals(Modals.SignalOnCallScheduleModal);

  const theme = useTheme();

  const [selectedEvent, setSelectedEvent] = useState<CalendarEventObject | null>(null);
  const [currentView, setCurrentView] = useState<CalendarView>(
    isMobile ? DEFAULT_MOBILE_CALENDAR_VIEW : DEFAULT_DESKTOP_CALENDAR_VIEW,
  );
  const defaultView = isMobile ? DEFAULT_MOBILE_CALENDAR_VIEW : DEFAULT_DESKTOP_CALENDAR_VIEW;
  const availableViews = useMemo(
    () => (isMobile ? MOBILE_CALENDAR_VIEWS : DESKTOP_CALENDAR_VIEWS),
    [isMobile],
  );
  const activeView = availableViews.includes(currentView) ? currentView : defaultView;
  const views = useMemo(
    () => ({
      month: false,
      week: availableViews.includes('week'),
      day: availableViews.includes('day'),
      agenda: availableViews.includes('agenda'),
    }),
    [availableViews],
  );

  const calendarFormats = useMemo<Formats>(() => {
    const formatTime = (
      date: Date,
      culture: string | undefined,
      calendarLocalizer?: DateLocalizer,
    ) => (calendarLocalizer ?? localizer).format(date, 'hh:mm a', culture).toUpperCase();

    return {
      eventTimeRangeFormat: (event, culture, calendarLocalizer) => {
        if (isAllDaySchedule(event)) {
          return ALL_DAY_EVENT_HEADER;
        }

        return `${formatTime(event.start, culture, calendarLocalizer)} - ${formatTime(
          event.end,
          culture,
          calendarLocalizer,
        )}`;
      },
      ...(isMobile
        ? {
            agendaTimeRangeFormat: (
              range: { start: Date; end: Date },
              culture: string | undefined,
              calendarLocalizer?: DateLocalizer,
            ) =>
              `${formatTime(range.start, culture, calendarLocalizer)}\n${formatTime(
                range.end,
                culture,
                calendarLocalizer,
              )}`,
          }
        : {}),
    } as const;
  }, [isMobile]);

  useEffect(() => {
    if (currentView !== activeView) {
      setCurrentView(activeView);
    }
  }, [activeView, currentView]);

  const showSignalManagementModalWithProps = useCallback(
    ({
      data,
      ...props
    }: { data: CalendarEventObject } & Partial<
      Omit<SignalOnCallScheduleModalProps, 'data'>
    >) => {
      showSignalManagementModal({
        partnerId,
        agents,
        timezone,
        events: existingEvents,
        onClose: () => setSelectedEvent(null),
        onSubmit: (event: OncallManagementScheduleMutationRecord, isEditing?: boolean) =>
          isEditing ? onUpdateEvent(event) : onCreateEvent(event),
        onDelete: (eventId: string) => onDeleteEvent(eventId),
        data,
        ...props,
      });
    },
    [
      agents,
      existingEvents,
      onCreateEvent,
      onDeleteEvent,
      onUpdateEvent,
      partnerId,
      showSignalManagementModal,
      timezone,
    ],
  );

  const allScheduleEvents = useMemo(() => {
    let result: CalendarEventObject[] = [];
    if (selectedEvent?.isTemporary) {
      result.push(selectedEvent);
    }

    if (existingEvents) {
      result = result.concat(mapScheduleToEvents(existingEvents));
    }

    /**
     * Events in calendar should be displayed in the timezone of the partner,
     * but on server they are stored in UTC.
     * We should convert them before displaying, except for ones that were just created
     * and are not yet saved on server.
     */
    return result.map((event) => {
      if (event.isTemporary) {
        return event;
      }
      const untilRule =
        event.recurrence && event.recurrence !== RecurrenceOptions.Once
          ? rrulestr(event.recurrence, {
              dtstart: event.start,
            })
          : undefined;

      return {
        ...event,
        start: convertDateTimeToDate(event.start, timezone),
        end: convertDateTimeToDate(event.end, timezone),
        until: untilRule?.options.until
          ? convertDateTimeToDate(untilRule.options.until, timezone)
          : undefined,
      };
    });
  }, [existingEvents, selectedEvent, timezone]);

  const [displayedEvents, setDisplayedEvents] = useState(allScheduleEvents);

  useEffect(() => {
    // Filter one-time events and generate recurring events for selected range.
    setDisplayedEvents(
      getDisplayedEventsForSelectedRange(allScheduleEvents, selectedDate, activeView),
    );
  }, [activeView, allScheduleEvents, selectedDate, timezone]);

  // Clicking on or dragging pressed mouse over the calendar slot creates a new schedule.
  const onSelectSlot = useCallback(
    (info: SlotInfo) => {
      if (!canManageSchedules || activeView === Views.AGENDA) {
        return;
      }

      /*
      Clicking or dragging cursor across the top row of React Big Calendar selects an all-day slot,
      which we should handle differently than regular slots which take less than a day.

      That said, dragging the mouse over the top row selects a slot that spans several days,
      what we don't support currently.
      This behavior cannot be disabled, so we consider any slot longer than 24 hours as an all-day slot.
      In case of dragging, we just use the last day of the selection as selected day.
      */
      const isAllDaySlot = isAllDaySlotSelected(info.start, info.end);

      /*
      React Big Calendar sets end time to the next day 00:00:00 if the all-day slot on top is clicked,
      but it should be set to 23:59:59.999 of the selected day to preserve the date.
    */
      const startTime = info.start;
      const endTime = isAllDaySlot
        ? dayjs(info.end).subtract(1, 'millisecond').toDate()
        : info.end;

      const isSameDay = dayjs(startTime).isSame(dayjs(endTime), 'day');

      const eventStart = isSameDay ? startTime : dayjs(endTime).startOf('day').toDate();
      const eventEnd = isAllDaySlot ? dayjs(eventStart).endOf('day').toDate() : endTime;

      const event = {
        start: eventStart,
        end: eventEnd,
        title: <SignalHeader variant="body2">New On-Call Schedule</SignalHeader>,
        isTemporary: true,
        recurrence: RecurrenceOptions.Once,
      } as CalendarEventObject;

      setSelectedEvent(event);
      showSignalManagementModalWithProps({
        data: event,
      });
    },
    [activeView, canManageSchedules, showSignalManagementModalWithProps],
  );

  const onSelectEvent = useCallback(
    (event: CalendarEventObject) => {
      if (!canManageSchedules) {
        return;
      }

      const todayStartDay = convertDateTimeToDate(dayjs().startOf('day').toDate(), timezone);

      if (event.start < todayStartDay) {
        return;
      }
      setSelectedEvent(event);
      showSignalManagementModalWithProps({
        data: event,
      });
    },
    [canManageSchedules, showSignalManagementModalWithProps, timezone],
  );

  const createEvent = useCallback(() => {
    if (!canManageSchedules) {
      return;
    }

    const start = dayjs().startOf('day').toDate();
    const end = dayjs().endOf('day').toDate();

    const event = {
      start,
      end,
      title: <SignalHeader variant="body2">New On-Call Schedule</SignalHeader>,
      isTemporary: true,
      recurrence: RecurrenceOptions.Once,
    } as CalendarEventObject;

    setSelectedEvent(event);

    showSignalManagementModalWithProps({
      data: event,
    });
  }, [canManageSchedules, showSignalManagementModalWithProps]);

  const eventPropHandler = useCallback(
    ({ primaryAgentId }: { primaryAgentId: string }) => {
      const primaryAgent = agents.find((agent) => agent.id === primaryAgentId);

      const style = {
        '--eventColor': primaryAgent
          ? pickColor(getFullName(primaryAgent))
          : theme.palette.secondary.main,
      } as React.CSSProperties;
      return { style };
    },
    [agents, theme.palette.secondary.main],
  );

  // Use RBC all-day semantics only in Agenda so Day/Week keep existing full-day block behavior.
  const allDayAccessor = useCallback(
    (event: CalendarEventObject) => activeView === Views.AGENDA && isAllDaySchedule(event),
    [activeView],
  );

  const components = useMemo(
    () => ({
      toolbar: () => (
        <SignalToolbar
          timezone={timezone}
          date={selectedDate}
          view={activeView}
          availableViews={availableViews}
          onChange={onDateChange}
          onViewChange={setCurrentView}
          onAddSchedule={createEvent}
          canManageSchedules={canManageSchedules}
        />
      ),
      eventWrapper: ({
        className,
        children,
        event,
      }: {
        // eslint-disable-next-line react/no-unused-prop-types
        children?: React.ReactNode;
      } & EventWrapperProps<CalendarEventObject>) => {
        return (
          <EventWrapper className={className} event={event}>
            {children}
          </EventWrapper>
        );
      },
    }),
    [
      canManageSchedules,
      selectedDate,
      activeView,
      availableViews,
      createEvent,
      onDateChange,
      setCurrentView,
      timezone,
    ],
  );

  /**
   * Override the current time for the calendar to use the partner's timezone
   * instead of the browser's timezone. This ensures the current time indicator
   * line aligns correctly with the schedules displayed in the partner's timezone.
   */
  const getNow = useCallback(() => {
    return convertDateTimeToDate(new Date(), timezone);
  }, [timezone]);

  /**
   * Apply a visual indicator to time slots that fall within the DST missing hour
   * in the browser's timezone. This helps users understand when a gap exists
   * due to DST "spring forward" transitions.
   */
  const slotPropGetter = (date: Date) => {
    const missingHour = getMissingDstHour(date);

    if (missingHour !== null && date.getHours() === missingHour + 1) {
      const isStartSlot = date.getMinutes() === 0;

      return {
        className: isStartSlot
          ? 'rbc-missing-hour-slot-start'
          : 'rbc-missing-hour-slot-follow',
      };
    }

    return {};
  };

  return (
    <CalendarWrapper>
      {hasLoadError && activeView === Views.AGENDA ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Unable to load agenda data. Please try again later.
        </Alert>
      ) : null}
      <Calendar
        events={displayedEvents}
        onSelectEvent={onSelectEvent}
        onSelectSlot={onSelectSlot}
        selectable={canManageSchedules ? activeView !== Views.AGENDA : false}
        showMultiDayTimes
        localizer={localizer}
        views={views}
        date={selectedDate}
        defaultView={defaultView}
        view={activeView}
        onView={(nextView) => {
          if (isCalendarView(nextView) && availableViews.includes(nextView)) {
            setCurrentView(nextView);
          }
        }}
        components={components}
        formats={calendarFormats}
        // This property is required, otherwise calendar throws warning when `date` is provided.
        onNavigate={noop}
        tooltipAccessor="tooltip"
        allDayAccessor={allDayAccessor}
        eventPropGetter={eventPropHandler}
        slotPropGetter={slotPropGetter}
        getNow={getNow}
      />
    </CalendarWrapper>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}
